import React, { useState } from "react";
import { message } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Login } from "../../routers/routeName";
import { mobileMenuList } from "../../routers/index";
import { getUserInfo } from "../../utils/localStorage";
import { getCompany, getLogout } from "../../api/user";
import { isPendingContractRenewal } from "../../constants/contract";
import styles from "./index.module.scss";

const menuIconMap: Record<string, string> = {
  个人中心: "https://web-data.zmlearn.com/image/jjLRTqZFGrqJ4sxZjWDnQ9/personal.png",
  结算管理: "https://web-data.zmlearn.com/image/jSG41Z3iLxmnXEju7LXiLe/jiesuan.png",
  名单管理: "https://web-data.zmlearn.com/image/qwnN9VaXgwYidKyqyt9uEw/mingdan.png",
  退出登录: "https://web-data.zmlearn.com/image/wXd6zJeVvqAWu2hoPMqxVh/logout.png",
};

const activeMenuIconMap: Record<string, string> = {
  个人中心: "https://web-data.zmlearn.com/image/qGitfu8QcJ3pP4yLSaf292/active_personal.png",
  结算管理: "https://web-data.zmlearn.com/image/8gZUncWGFUPEEP4rYxkCPU/active_jiesuan.png",
  名单管理: "https://web-data.zmlearn.com/image/nZYvKkh5G5amwMUGPNspsy/active_mingdan.png",
  退出登录: "https://web-data.zmlearn.com/image/2npD3Gxsvp3A5WAK5uPGPH/active_logout.png",
};

export default function MobileLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo: any = getUserInfo();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutVisible, setLogoutVisible] = useState(false);
  const [agreementPending, setAgreementPending] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const currentMenuName = mobileMenuList.find((item) => item.path === location.pathname)?.name
    || "个人中心";

  React.useEffect(() => {
    if (!userInfo?.accessToken) {
      navigate(Login);
      return;
    }

    const checkAgreementStatus = async () => {
      const [suc] = await getCompany();
      const company = suc?.data;
      const pending = company?.signStatusDesc === "未签约"
        || isPendingContractRenewal(company);
      setAgreementPending(pending);
      setAgreementChecked(true);

      if (pending && location.pathname !== "/m/personalCenter") {
        navigate("/m/personalCenter", { replace: true });
      }
    };

    checkAgreementStatus();
  }, []);

  React.useEffect(() => {
    if (agreementChecked && agreementPending && location.pathname !== "/m/personalCenter") {
      navigate("/m/personalCenter", { replace: true });
    }
  }, [agreementChecked, agreementPending, location.pathname, navigate]);

  const handleLogout = async () => {
    const [suc] = await getLogout();
    if (suc) {
      localStorage.removeItem("userInfo");
      setMenuOpen(false);
      setLogoutVisible(false);
      message.success({ content: "退出登录成功" });
      navigate(Login, { replace: true });
    }
  };

  const openLogoutConfirm = () => {
    setMenuOpen(false);
    setLogoutVisible(true);
  };

  const menuItems = [
    ...mobileMenuList.map((item) => ({
      activeIcon: activeMenuIconMap[item.name || ""],
      icon: menuIconMap[item.name || ""],
      label: item.name,
      path: item.path,
      onClick: () => {
        setMenuOpen(false);
        if (agreementPending && item.path !== "/m/personalCenter") {
          message.warning("请先在个人中心确认协议");
          navigate("/m/personalCenter", { replace: true });
          return;
        }
        navigate(item.path);
      },
    })),
    {
      activeIcon: activeMenuIconMap["退出登录"],
      icon: menuIconMap["退出登录"],
      label: "退出登录",
      path: undefined,
      onClick: openLogoutConfirm,
    },
  ];

  const isMenuItemActive = (item: typeof menuItems[number]) =>
    item.path === location.pathname
    || (item.label === "退出登录" && logoutVisible);

  const getMenuItemClassName = (item: typeof menuItems[number]) => {
    return isMenuItemActive(item)
      ? `${styles.menuItem} ${styles.menuItemActive}`
      : styles.menuItem;
  };

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <strong>AirClass</strong>
          <span>商务合作伙伴工作台</span>
        </div>
        <div className={styles.actions}>
          <button className={styles.userName} onClick={openLogoutConfirm} type="button">
            {userInfo?.name || userInfo?.companyName || "用户名"}
          </button>
          <button
            aria-expanded={menuOpen}
            aria-label="打开更多菜单"
            className={styles.centerButton}
            onClick={() => setMenuOpen(true)}
            type="button"
          >
            {currentMenuName}
            <span className={styles.downIcon} />
          </button>
        </div>
      </header>
      {menuOpen && (
        <div className={styles.menuLayer}>
          <button
            aria-label="关闭更多菜单"
            className={styles.menuBackdrop}
            onClick={() => setMenuOpen(false)}
            type="button"
          />
          <div className={styles.menuPanel}>
            <div className={styles.closeRow}>
              <button aria-label="关闭菜单" className={styles.closeButton} onClick={() => setMenuOpen(false)} type="button" />
            </div>
            {menuItems.map((item) => (
              <button
                className={getMenuItemClassName(item)}
                key={item.label}
                onClick={item.onClick}
                type="button"
              >
                <img
                  alt=""
                  aria-hidden="true"
                  className={styles.menuIcon}
                  src={isMenuItemActive(item) ? item.activeIcon : item.icon}
                />
                {item.label}
              </button>
          ))}
          </div>
        </div>
      )}
      {logoutVisible && (
        <div className={styles.logoutLayer}>
          <button
            aria-label="取消退出登录"
            className={styles.logoutMask}
            onClick={() => setLogoutVisible(false)}
            type="button"
          />
          <div className={styles.logoutDialog}>
            <p>确定要退出吗？</p>
            <div className={styles.logoutActions}>
              <button className={styles.logoutCancel} onClick={() => setLogoutVisible(false)} type="button">
                取消
              </button>
              <button className={styles.logoutOk} onClick={handleLogout} type="button">
                确定
              </button>
            </div>
          </div>
        </div>
      )}
      <main className={styles.content}>
        {(agreementChecked || location.pathname === "/m/personalCenter") && <Outlet />}
      </main>
    </div>
  );
}
